import { FC, ReactNode } from "react";
import { Building, Check } from "lucide-react";
import { CopropertyDto } from "api/coproperty";
import {
  CLAIM_GUARANTEE_LABELS,
  claimsSituationSummary,
} from "features/subscription-process/coproperty/CopropertyClaimsForm";
import { OCCUPANCY_OPTIONS } from "features/subscription-process/coproperty/CopropertyFieldControls";
import { CopropertyIdentityCard } from "features/subscription-process/coproperty/CopropertyIdentityCard";
import { CopropertyRecapPanel } from "features/subscription-process/coproperty/CopropertyRecapPanel";
import { MRI_ROUTES } from "features/subscription-process/utils/mriRoutes";

/** Surface en m² au format français ("2 500 m²"). */
const formatArea = (value: number): string => `${Math.round(value).toLocaleString("fr-FR")} m²`;

interface SummarySectionProps {
  title: string;
  /** Action en tête de section (lien « Modifier », bouton « Afficher le questionnaire »…). */
  action?: ReactNode;
  children: ReactNode;
}

/** Carte titrée du récapitulatif copropriété, partagée entre le panneau et le détail contrat. */
export const SummarySection: FC<SummarySectionProps> = ({ title, action, children }) => (
  <section className="rounded-xl border bg-white p-4">
    <div className="mb-3 flex items-center justify-between gap-4">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">{title}</h3>
      {action}
    </div>
    {children}
  </section>
);

const EditLink: FC<{ onClick: () => void }> = ({ onClick }) => (
  <button type="button" onClick={onClick} className="text-[13px] font-medium text-primary hover:underline">
    Modifier
  </button>
);

/** Réponse oui/non déclarative : « Non renseigné » tant que la question n'a pas été tranchée. */
const yesNoLabel = (value: boolean | null | undefined): string =>
  value == null ? "Non renseigné" : value ? "Oui" : "Non";

/** Ligne libellé / valeur du récapitulatif, au format des lignes existantes. */
const SummaryRow: FC<{ label: string; value: string }> = ({ label, value }) => (
  <>
    <dt className="text-gray-500">{label}</dt>
    <dd className="text-right font-medium text-gray-900">{value}</dd>
  </>
);

/** Contrat en cours (prime, exercice), prochaine AG et historique d'assurance des 36 derniers mois. */
export const ComplementsSection: FC<{ coproperty: CopropertyDto; action?: ReactNode }> = ({ coproperty, action }) => {
  const complements = coproperty.complements;
  const hasComplements =
    complements != null &&
    (complements.currentPremiumExclTax != null || complements.premiumYear != null || complements.nextGeneralMeetingDate != null);
  const interruptionPeriod =
    complements?.coverageInterruptionStart != null && complements?.coverageInterruptionEnd != null
      ? `du ${complements.coverageInterruptionStart.toLocaleDateString("fr-FR")} au ${complements.coverageInterruptionEnd.toLocaleDateString("fr-FR")}`
      : null;

  return (
    <SummarySection title="Contrat en cours et prochaine AG" action={action}>
      {hasComplements ? (
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          {complements?.currentPremiumExclTax != null && (
            <>
              <dt className="text-gray-500">Prime actuelle HT</dt>
              <dd className="text-right font-medium text-gray-900">{complements.currentPremiumExclTax.toLocaleString("fr-FR")} €</dd>
            </>
          )}
          {complements?.premiumYear != null && (
            <>
              <dt className="text-gray-500">Exercice</dt>
              <dd className="text-right font-medium text-gray-900">{complements.premiumYear}</dd>
            </>
          )}
          {complements?.nextGeneralMeetingDate != null && (
            <>
              <dt className="text-gray-500">Prochaine AG</dt>
              <dd className="text-right font-medium text-gray-900">{complements.nextGeneralMeetingDate.toLocaleDateString("fr-FR")}</dd>
            </>
          )}
        </dl>
      ) : (
        <p className="text-sm text-gray-500">Rien de renseigné.</p>
      )}

      <div className="mt-3 border-t border-gray-100 pt-3">
        <h4 className="mb-2 text-[13px] font-semibold text-gray-700">Historique d'assurance</h4>
        <dl className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
          <SummaryRow
            label="Immeuble assuré au cours des 36 derniers mois"
            value={yesNoLabel(complements?.insuredLast36Months)}
          />
          <SummaryRow
            label="Interruption de garantie au cours des 36 derniers mois"
            value={yesNoLabel(complements?.coverageInterruptionLast36Months)}
          />
          {interruptionPeriod != null && <SummaryRow label="Période d'interruption" value={interruptionPeriod} />}
          <SummaryRow
            label="Résiliation au cours des 36 derniers mois"
            value={yesNoLabel(complements?.terminationLast36Months)}
          />
          {complements?.terminationReason && (
            <SummaryRow label="Motif de la résiliation" value={complements.terminationReason} />
          )}
        </dl>
      </div>
    </SummarySection>
  );
};

/** Sinistralité déclarée, une ligne par garantie. */
export const ClaimsSection: FC<{ coproperty: CopropertyDto; action?: ReactNode; footer?: ReactNode }> = ({
  coproperty,
  action,
  footer,
}) => {
  const history = coproperty.claimsHistory;
  const claims = history?.claims ?? [];

  return (
    <SummarySection title="Sinistralité déclarée" action={action}>
      <p className="mb-3 text-sm font-medium text-gray-900">{claimsSituationSummary(history)}</p>

      {claims.length > 0 && (
        <ul className="divide-y divide-gray-100 text-sm">
          {claims.map((claim) => (
            <li key={claim.id} className="flex items-center justify-between gap-3 py-2">
              <span className="text-gray-900">
                {claim.guaranteeCode ? CLAIM_GUARANTEE_LABELS[claim.guaranteeCode] : "Garantie"}
              </span>
              <span className="text-gray-600">
                {[
                  `${claim.count ?? 0} sinistre${(claim.count ?? 0) > 1 ? "s" : ""}`,
                  claim.totalAmount != null && `${claim.totalAmount.toLocaleString("fr-FR")} €`,
                ]
                  .filter(Boolean)
                  .join(" · ")}
              </span>
            </li>
          ))}
        </ul>
      )}

      {footer}
    </SummarySection>
  );
};

/** Sections du récapitulatif qui se modifient une par une à la contractualisation. */
export type MriCopropertySection = "survey" | "buildings" | "complements" | "claims";

const ALL_EDITABLE_SECTIONS: readonly MriCopropertySection[] = ["survey", "buildings", "complements", "claims"];

interface CopropertySummaryPanelProps {
  coproperty: CopropertyDto;
  riskId: string;
  /** Ferme le panneau et navigue vers la sous-étape (route complète). */
  onNavigate?: (route: string) => void;
  /**
   * Consultation seule : aucun lien « Modifier » ni « Changer ». La modification de la
   * copropriété se fait à la contractualisation, plus depuis la tarification.
   */
  readOnly?: boolean;
  /**
   * Modification par sections, sans quitter la page : chaque section éditable affiche un
   * lien « Modifier » qui ouvre son sous-panneau au lieu de renvoyer au parcours.
   */
  onEditSection?: (section: MriCopropertySection) => void;
  /** Sections qui portent le lien « Modifier » ; toutes par défaut. */
  editableSections?: readonly MriCopropertySection[];
  /** Contenu ajouté en bas de la section « Sinistralité déclarée » (pièce attendue, engagements). */
  claimsFooter?: ReactNode;
}

/**
 * Récapitulatif de la copropriété ouvert depuis l'en-tête du tarificateur : tout ce qui
 * a été saisi dans la création du dossier, section par section. En consultation seule
 * (`readOnly`), aucune section ne renvoie vers le parcours ; avec `onEditSection`, chaque
 * section se modifie dans son propre sous-panneau.
 */
export const CopropertySummaryPanel: FC<CopropertySummaryPanelProps> = ({
  coproperty,
  riskId,
  onNavigate,
  readOnly = false,
  onEditSection,
  editableSections = ALL_EDITABLE_SECTIONS,
  claimsFooter,
}) => {
  const editAction = (section: MriCopropertySection, base: string) => {
    if (onEditSection) {
      return editableSections.includes(section) ? <EditLink onClick={() => onEditSection(section)} /> : undefined;
    }
    if (readOnly || !onNavigate) return undefined;
    return <EditLink onClick={() => onNavigate(`${base}/${riskId}`)} />;
  };
  const buildings = coproperty.buildings ?? [];

  return (
    <div className="flex flex-col gap-4">
      <CopropertyIdentityCard
        coproperty={coproperty}
        onChange={
          readOnly || onEditSection || !onNavigate
            ? undefined
            : () => onNavigate(`${MRI_ROUTES.copropertyRegistration}/${riskId}`)
        }
      />

      <CopropertyRecapPanel
        coproperty={coproperty}
        hideIdentity
        showRetainedArea
        checklist={[
          { label: "Copropriété rattachée", status: "done" },
          { label: "Questionnaire éligible", status: "done" },
          { label: "Bâtiments décrits", status: buildings.length > 0 ? "done" : "todo" },
        ]}
      />

      <SummarySection title="Questionnaire d'appréciation du risque" action={editAction("survey", MRI_ROUTES.copropertySurvey)}>
        <p className="flex items-center gap-2 text-sm text-gray-700">
          <Check className="h-4 w-4 text-green-700" />
          Aucune exclusion déclenchée — le dossier est arrivé à la tarification.
        </p>
      </SummarySection>

      <SummarySection title={`Bâtiments (${buildings.length})`} action={editAction("buildings", MRI_ROUTES.copropertyBuildings)}>
        {buildings.length === 0 ? (
          <p className="text-sm text-gray-500">Aucun bâtiment décrit.</p>
        ) : (
          <ul className="divide-y divide-gray-100">
            {buildings.map((building, index) => (
              <li key={building.id ?? index} className="flex items-center gap-3 py-2 text-sm">
                <Building className="h-4 w-4 flex-none text-primary" />
                <span className="font-medium text-gray-900">Bâtiment {index + 1}</span>
                <span className="text-gray-600">
                  {[
                    building.area != null && formatArea(building.area),
                    building.floorsCount != null &&
                      (building.floorsCount === 0 ? "rez-de-chaussée seul" : `R+${building.floorsCount}`),
                    building.basementsCount != null && building.basementsCount > 0 && `${building.basementsCount} sous-sol${building.basementsCount > 1 ? "s" : ""}`,
                    OCCUPANCY_OPTIONS.find((o) => o.value === building.occupancy)?.label?.toLowerCase(),
                  ]
                    .filter(Boolean)
                    .join(" · ")}
                </span>
              </li>
            ))}
          </ul>
        )}
      </SummarySection>

      <ComplementsSection coproperty={coproperty} action={editAction("complements", MRI_ROUTES.copropertyComplements)} />

      <ClaimsSection
        coproperty={coproperty}
        action={editAction("claims", MRI_ROUTES.copropertyClaims)}
        footer={claimsFooter}
      />
    </div>
  );
};
